import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { connect } from 'react-redux';
import axiosInstance from '../general/axiosInstance';
import PaymentFormModal from './paymentFormModal';

/**
 * Aviso de cuentas por pagar (una sola línea, fija sobre el menú principal).
 * Solo para usuarios del lado proveedor: para clientes (y superadmin) el componente no renderiza ni
 * hace NINGUNA petición. El backend además responde 403 a no-proveedores.
 * No se puede cerrar: desaparece cuando el pago se reporta (queda "en verificación") y se confirma.
 */
function PayablesNotice({ isLoggedIn, is_provider, roles }) {
    const eligible = !!isLoggedIn && !!is_provider && !(roles || []).includes('superadmin');

    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [targetId, setTargetId] = useState(null);
    const barRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/payments/notice');
            setItems(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setItems([]); // el aviso es informativo: un fallo no debe romper la app
        }
    }, []);

    useEffect(() => {
        if (eligible) load();
        else setItems([]);
    }, [eligible, load]);

    const visible = eligible && items.length > 0;

    // La franja es fija sobre el menú: publica su alto en --payables-h para que el menú (top) y el
    // contenido (padding-top) se desplacen lo justo. Se limpia al ocultarse o desmontarse.
    useEffect(() => {
        const root = document.documentElement;
        const el = barRef.current;
        if (!visible || !el) {
            root.style.setProperty('--payables-h', '0px');
            return undefined;
        }
        const update = () => root.style.setProperty('--payables-h', `${el.offsetHeight}px`);
        update();
        let observer;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(update);
            observer.observe(el);
        } else {
            window.addEventListener('resize', update);
        }
        return () => {
            if (observer) observer.disconnect();
            else window.removeEventListener('resize', update);
            root.style.setProperty('--payables-h', '0px');
        };
    }, [visible, items]);

    if (!visible) return null;

    const isPending = (i) => i.last_report?.status === 'pending';
    const actionable = items.filter((i) => !isPending(i));
    const pending = items.filter(isPending);
    const target = actionable.find((i) => i.id === targetId) || actionable[0];

    const contractLabel = (i) => `${i.ext_id}${i.customer?.name ? ' — ' + i.customer.name : ''}`;

    // Texto de la única línea.
    let text;
    if (actionable.length === 0) {
        text = `Pago reportado, en verificación: ${pending.map((i) => `${contractLabel(i)} (ref. ${i.last_report.reference})`).join(' · ')}`;
    } else if (actionable.length === 1) {
        const a = actionable[0];
        text = `Tienes cuentas por pagar con Abi: ${contractLabel(a)}`;
        if (a.note) text += ` · ${a.note}`;
        if (a.last_report?.status === 'rejected') {
            text += ` · Reporte rechazado${a.last_report.review_note ? ': ' + a.last_report.review_note : ''}`;
        }
        if (pending.length) text += ` · ${pending.length} en verificación`;
    } else {
        text = `Tienes cuentas por pagar con Abi en ${actionable.length} contratos`;
        if (pending.length) text += ` · ${pending.length} en verificación`;
    }

    const submitReport = async (values) => {
        try {
            await axiosInstance.post(`/payments/agreements/${target.id}/reports`, values);
        } catch (err) {
            // 409: otro usuario ya reportó o Abi ya lo resolvió -> refrescar el estado real.
            if (err?.response?.status === 409) load();
            throw err;
        }
        setOpen(false);
        await load();
    };

    return (
        <>
            <div ref={barRef} className={`payables-bar no-print payables-line ${actionable.length ? 'payables-line--warning' : 'payables-line--info'}`}>
                <i className={`fa ${actionable.length ? 'fa-exclamation-triangle' : 'fa-clock-o'}`} />
                <span className="payables-line__text" title={text}>{text}</span>
                {actionable.length > 0 && (
                    <Button size="sm" variant="warning" className="payables-line__btn"
                        onClick={() => { setTargetId(actionable[0].id); setOpen(true); }}>
                        Ya pagué, reportar
                    </Button>
                )}
            </div>

            <PaymentFormModal
                show={open && !!target}
                onHide={() => setOpen(false)}
                title="Reportar pago"
                subtitle={target ? `Contrato ${contractLabel(target)}. Abi verificará el pago y actualizará el estado.` : ''}
                submitLabel="Enviar reporte"
                onSubmit={submitReport}
            >
                {actionable.length > 1 && (
                    <Form.Group className="mb-3">
                        <Form.Label>Contrato</Form.Label>
                        <Form.Select value={target ? target.id : ''} onChange={(e) => setTargetId(Number(e.target.value))}>
                            {actionable.map((i) => <option key={i.id} value={i.id}>{contractLabel(i)}</option>)}
                        </Form.Select>
                    </Form.Group>
                )}
            </PaymentFormModal>
        </>
    );
}

const mapStateToProps = (state) => ({
    isLoggedIn: state.isLoggedIn,
    is_provider: state.is_provider,
    roles: state.roles,
});

export default connect(mapStateToProps)(PayablesNotice);
